import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { openModal, closeModal } from 'actions/modalActions'
import { cancelTokenRefresh, doLogout } from 'actions/authActions'
import debug from 'Helper/debugHelpers'

const InactivityTimer = props => {
  const dispatch = useDispatch()
  const [inactivityTimeMin, setInactivityTimeMin] = useState(0)
  const [loggingOut, setLoggingOut] = useState(false)
  // const loggingOutRef = useRef(false)
  const maxInactivityTimeMin = parseInt(props.inactivityTimerMins)
  const timerRef = useRef()
  const resetRef = useRef()
  const incrementRef = useRef()
  const inactivityTimeMinRef = useRef(inactivityTimeMin)
  const [inactivityTimerRunning, setInactivityTimerRunning] = useState(maxInactivityTimeMin !== 0)

  useEffect(() => {
    setInactivityTimerRunning(maxInactivityTimeMin !== 0)
  },[maxInactivityTimeMin])

  useEffect(() => {
    inactivityTimeMinRef.current = inactivityTimeMin
  }, [inactivityTimeMin])

  useEffect(() => {
    // do the work of starting and stopping the timer when the timmerRunning flag changes
    if(inactivityTimerRunning) {
      timerRef.current = setInterval(incrementRef.current, 60000) // 1 minute
      document.addEventListener('mousemove', resetRef.current, true)
      document.addEventListener('keypress', resetRef.current, true)
    }
    
    return () => {
      if(inactivityTimerRunning) {
        setInactivityTimeMin(0)
        if (timerRef.current) {
          clearInterval(timerRef.current)
        }
        document.removeEventListener('mousemove', resetRef.current)
        document.removeEventListener('keypress', resetRef.current)
      }
    }
  }, [inactivityTimerRunning, resetRef])

  resetRef.current = () => {
    // set time back to 0
    setInactivityTimeMin(0)
    //reset the current timer
    clearInterval(timerRef.current)
    if(inactivityTimerRunning) {
      // if its supposed to be running, restart it.
      timerRef.current = setInterval(incrementRef.current, 60000)
    }
  }

  incrementRef.current = () => {
    setInactivityTimeMin(inactivityTimeMinRef.current+1)
  }

  const restartInactivityTimer = () => {
    setInactivityTimerRunning(maxInactivityTimeMin !== 0)
    resetRef.current()
  }

  useEffect(() => {
    // do the work to check what should happen whenever the timer increments
    if (maxInactivityTimeMin > 0 && inactivityTimerRunning && inactivityTimeMin >= maxInactivityTimeMin && !loggingOut) {
      setInactivityTimerRunning(false) // stop running this time since the popup is handling things now
      // show popup
      dispatch(openModal('inactivity_alert_modal', {
        title: 'Inactivity Alert!',
        content: <InactivityModal
                  maxInactivityTimeMin={maxInactivityTimeMin}
                  restartInactivityTimer={restartInactivityTimer}
                  logoutCallback={() => { 
                    setLoggingOut(true)
                    dispatch(closeModal('inactivity_alert_modal')) 
                  }}
                />,
        closeable: false
      }))
    }
  }, [inactivityTimerRunning, maxInactivityTimeMin, inactivityTimeMin, loggingOut])

  useEffect(() => {
    return () => {
      // Make sure timers are cleared
      setInactivityTimerRunning(false)
      timerRef.current = () => null
      resetRef.current = () => null
    }
  }, [])

  return props.children
}

export default InactivityTimer

const InactivityModal = ({ maxInactivityTimeMin, logoutCallback, restartInactivityTimer }) => {
  const maxPopupTimeSec = 60
  const dispatch = useDispatch()
  const [popupTimerSec, setPopupTimerSec] = useState(0)
  const popupTimerSecRef = useRef()
  const popupTimerRef = useRef()

  useEffect(() => {
    // this needs to use a ref because it needs to be read in a setInterval callback. It only gets the
    // initial instance of the callback so updating the value directly wont be seen.
    popupTimerSecRef.current = popupTimerSec
  }, [popupTimerSec])

  useEffect(() => {
    popupTimerRef.current = setInterval(popupTimerIncrement, 1000)
    return () => {
      if (popupTimerRef.current) {
        clearInterval(popupTimerRef.current)
      }
    }
  }, [])

  const popupTimerIncrement = useCallback(() => {
    const newPopupTimeSec = popupTimerSecRef.current + 1
    setPopupTimerSec(newPopupTimeSec)
    if (newPopupTimeSec >= maxPopupTimeSec) {
      logoutCallback()
      // cancel the popup timer
      clearInterval(popupTimerRef.current)
      // cancel any token refresh that may be in progress
      dispatch(cancelTokenRefresh())
      // push them to the login path
      debug.log('doing inactivity logout',debug.logLevels.AUTH)
      dispatch(doLogout())
    }
  }, [popupTimerSecRef, maxPopupTimeSec, setPopupTimerSec])

  const inactivityContinueButton = useCallback(() => {
    // clear the popup timer
    clearInterval(popupTimerRef.current)
    // set the popup timer to null
    popupTimerRef.current = null
    setPopupTimerSec(null)
    dispatch(closeModal('inactivity_alert_modal'))
    restartInactivityTimer()
  }, [setPopupTimerSec, restartInactivityTimer])

  return (
    <div>
      <div>
        This terminal has been inactive for more than{' '}
        {maxInactivityTimeMin} {(maxInactivityTimeMin === 1 ? 'minute' : 'minutes')}. <br />
        Please click Continue in the next
        <div
          style={{
            color: 'var(--red-color)',
            display: 'inline',
            paddingLeft: '.3em'
          }}
        >
          {maxPopupTimeSec - popupTimerSec}
        </div>{' '}
        seconds or you will be automatically logged out.
      </div>
      <button
        style={{ marginTop: '25px' }}
        className="btn btn-success"
        onClick={inactivityContinueButton}
      >
        Continue
      </button>
    </div>
  )
}
